/**
 * Word Builder drill strategy. A Word Builder step plays a Vocabulary stack
 * and grades like Vocabulary (the built word is a typed answer), so the
 * strategy is Vocabulary's; only the solve card differs: letter tiles.
 */
import { detectLanguage } from 'src/shared/utilities/string';
import { vocabularyStrategy } from './vocabulary';
import type { DrillStrategy } from './types';

export const wordBuilderStrategy: DrillStrategy<'WordBuilder'> = {
  ...(vocabularyStrategy as unknown as DrillStrategy<'WordBuilder'>),
  kind: 'WordBuilder',
};

/** Decoy letters when the drill sets no number_of_options. */
export const DEFAULT_DECOYS = 3;

const ALPHABETS = {
  en: 'abcdefghijklmnopqrstuvwxyz',
  uk: 'абвгґдеєжзиіїйклмнопрстуфхцчшщьюя',
  he: 'אבגדהוזחטיכלמנסעפצקרשת',
} as const;

const LETTER = /\p{L}/u;

/** A position of the answer: a letter to place, or a fixed space/sign. */
export type WordBuilderSlot = { fixed: true; char: string } | { fixed: false };
export type WordBuilderTile = { id: number; char: string };

/**
 * The board for `answer`: its slots (letters to place; spaces and signs are
 * fixed) and the shuffled tiles — the answer's letters plus `decoys` letters
 * of the same alphabet that are not in the answer.
 */
export function wordBuilderBoard(
  answer: string,
  decoys = DEFAULT_DECOYS,
  random: () => number = Math.random,
): { slots: WordBuilderSlot[]; tiles: WordBuilderTile[] } {
  const chars = [...answer.trim()];
  const slots: WordBuilderSlot[] = chars.map((char) =>
    LETTER.test(char) ? { fixed: false } : { fixed: true, char },
  );
  const letters = chars.filter((char) => LETTER.test(char));

  const lang = detectLanguage(answer);
  const alphabet = [...ALPHABETS[lang === 'unknown' ? 'en' : lang]];
  const used = new Set(letters.map((c) => c.toLowerCase()));
  const pool = alphabet.filter((c) => !used.has(c));
  const extra: string[] = [];
  for (let i = 0; i < Math.max(0, decoys) && pool.length; i++) {
    extra.push(...pool.splice(Math.floor(random() * pool.length), 1));
  }

  const tiles = [...letters, ...extra].map((char, id) => ({ id, char }));
  // Fisher–Yates with the injected random.
  for (let i = tiles.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [tiles[i], tiles[j]] = [tiles[j]!, tiles[i]!];
  }
  return { slots, tiles };
}

/** The answer text: placed letters in order, fixed characters in place. */
export function wordBuilderText(
  slots: readonly WordBuilderSlot[],
  placed: readonly string[],
): string {
  let next = 0;
  return slots
    .map((slot) => (slot.fixed ? slot.char : (placed[next++] ?? '')))
    .join('');
}

/** How many letters the answer needs. */
export function wordBuilderLetterCount(slots: readonly WordBuilderSlot[]) {
  return slots.filter((slot) => !slot.fixed).length;
}
